import { Button, Form, Modal, Col, Row, notification, Space } from "antd";
import { EditOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";
import React, { useState, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useMutation } from "@apollo/client/react";

import { useMode, SingleOrderContextProvider } from "../../organisms";
import { SupportedControls } from "../../molecules";
import { UPDATE_ITEM, GET_ITEMS } from "../../../graphql/items";
import {
  getPayrollUpdateMutation,
  getPayrollListQueryName,
} from "../../../graphql/payroll";

export const FieldGroup: React.FC<{ control: any; allControls?: any[] }> = ({
    control,
    allControls = [],
}) => {
    const { mode, global_actions, orderId, patchUrl, dataType } = useMode();
    const [open, setOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const formContext = useFormContext();


    // Find child controls by their UUIDs
    const childControls = useMemo(() => {
        if (!control.children || !Array.isArray(control.children)) {
            return [];
        }
        return control.children
            .map((childUuid: string) =>
                allControls.find((c: any) => c.uuid === childUuid)
            )
            .filter(Boolean)
            .slice().sort((a: any, b: any) => a.position - b.position);
    }, [control.children, allControls]);

    // Generate summary of child values for display
    const summary = useMemo(() => {
        if (!formContext || childControls.length === 0) {
            return "";
        }
        const values = formContext.getValues();
        const summaryParts = childControls
            .slice(0, 3)
            .map((child: any) => {
                const value = values[child.uuid];
                if (value && value !== "") {
                    return `${child.field_name}: ${value}`;
                }
                return null;
            })
            .filter(Boolean);

        if (childControls.length > 3) {
            summaryParts.push(`+${childControls.length - 3} więcej`);
        }

        return summaryParts.join(" | ");
    }, [childControls, formContext]);

    const updateMutation = getPayrollUpdateMutation(dataType) ?? UPDATE_ITEM;
    const refetchQueryName = getPayrollListQueryName(dataType) ?? "Items";

    const [updateItem, { loading: updateLoading }] = useMutation(updateMutation, {
        refetchQueries: [refetchQueryName],
        onCompleted: () => {
            notification.success({
                message: "Zapisano zmiany",
                style: { background: "#52c41a30" },
            });
            setIsEditing(false);
            setOpen(false);
        },
        onError: () => {
            notification.error({
                message: "Błąd zapisu danych",
                style: { background: "#ff4d4f30" },
            });
        },
    });

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setIsEditing(false);
        setOpen(false);
    };

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleSave = () => {
        if (!formContext || !orderId) return;
        formContext.handleSubmit((data) => {
            updateItem({ variables: { input: { id: Number(orderId), data } } });
        }, console.error)();
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
    };

    // Determine if children should be editable
    const childWriteable = isEditing || (mode === "edit" && control.writeable);

    // Build modal footer based on editing state
    const modalFooter = () => {
        if (isEditing) {
            return (
                <Space>
                    <Button
                        icon={<CloseOutlined />}
                        onClick={handleCancelEdit}
                    >
                        Anuluj
                    </Button>
                    <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={handleSave}
                        loading={updateLoading}
                    >
                        Zapisz
                    </Button>
                </Space>
            );
        }

        // In read mode within the modal: show Edytuj button if user has write access
        if (control.writeable && mode !== "edit") {
            return (
                <Button
                    type="primary"
                    ghost
                    icon={<EditOutlined />}
                    onClick={handleEdit}
                >
                    Edytuj
                </Button>
            );
        }

        // Global edit mode – close button only (save via global actions)
        return (
            <Button onClick={handleClose}>
                Zamknij
            </Button>
        );
    };

    return (
        <>
            <Form.Item
                label={mode === "edit" ? control.field_name : undefined}
                tooltip={mode === "edit" ? control.description : undefined}
                style={{ margin: 0, marginTop: global_actions ? "" : "30px" }}
            >
                <Button
                    type={mode === "edit" ? "primary" : "default"}
                    ghost={mode === "edit"}
                    disabled={mode === "edit" && !control.writeable}
                    onClick={handleOpen}
                    style={{ width: "100%" }}
                >
                    {control.field_name}
                </Button>
            </Form.Item>
            <Modal
                title={control.field_name}
                centered
                open={open}
                onCancel={handleClose}
                footer={modalFooter()}
                width={800}
                destroyOnClose
            >
                <SingleOrderContextProvider key={isEditing ? "editing" : "reading"} disableReadMode={isEditing || mode === "edit"} orderId={orderId} patchUrl={patchUrl} dataType={dataType}>
                    <Form layout="vertical">
                        <Row gutter={16}>
                            {childControls.map((childControl: any) => (
                                <Col span={childControl.displaying_size} key={childControl.uuid}>
                                    <SupportedControls
                                        control={{
                                            ...childControl,
                                            writeable: control.writeable && childControl.writeable !== false,
                                        }}
                                    />
                                </Col>
                            ))}
                        </Row>
                    </Form>
                </SingleOrderContextProvider>
            </Modal>
        </>
    );
};
